import { useState, type ReactNode } from "react";
import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import { cn } from "@akina/utils";
import { Separator } from "./Separator";
import { Text } from "./Text";

type DropdownOption<T> = {
  id?: string;
  label: string | ReactNode;
  value: T;
}

export type DropdownProps<T> = {
  trigger: ReactNode;
  className?: string;
  contentClassName?: string;
  children?: ReactNode;
  options?: (DropdownOption<T> | DropdownOption<T>[])[];
  onSelect?: (option: DropdownOption<T>) => void;
  content?: {
    side?: "top" | "right" | "bottom" | "left";
    sideOffset?: number;
    align?: "start" | "center" | "end";
    alignOffset?: number;
    arrowPadding?: number;
  };
  withArrow?: boolean;
  withSearch?: boolean;
  withScroll?: boolean;
  modal?: boolean;
  noItemPadding?: boolean;
  itemClassName?: string;
  getOptionTemplate?: (o: DropdownOption<T>) => ReactNode;
  searchPlaceholder?: string;
}

export const Dropdown = <T,>({
  className,
  contentClassName,
  trigger,
  options,
  onSelect,
  content,
  children,
  withArrow = false,
  withSearch = false,
  withScroll = false,
  modal = false,
  noItemPadding = false,
  itemClassName,
  getOptionTemplate,
  searchPlaceholder,
}: DropdownProps<T>) => {
  const [searchValue, setSearchValue] = useState("");

  const filter = () => {
    if (!withSearch) return options;
    if (!searchValue.trim()) return options;

    return options?.filter(o => {
      if (Array.isArray(o)) {
        return o.filter(o => {
          if (typeof o.label === "string") {
            return o.label.trim().toLowerCase().indexOf(searchValue.trim().toLowerCase()) !== -1;
          }
          return false;
        });
      }
      if (typeof o.label === "string") {
        return o.label.trim().toLowerCase().indexOf(searchValue.trim().toLowerCase()) !== -1;
      }

      return false;
    });
  };

  return (
    <RadixDropdownMenu.Root modal={modal}>
      <RadixDropdownMenu.Trigger asChild className={className}>
        {trigger}
      </RadixDropdownMenu.Trigger>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          className={cn("z-40 min-w-50 rounded-2xl bg-white shadow-lg will-change-[opacity,transform] data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade", contentClassName)}
          {...content}>
          <div
            onWheelCapture={e => e.stopPropagation()}
            className={cn("max-h-75 overflow-scroll py-3", withScroll ? "" : "no-scrollbar")}>
            <div className="px-4">{children}</div>

            {withSearch ? (
              <input
                placeholder={searchPlaceholder}
                className="mx-2 mb-1 rounded-md bg-gray-800/10 px-2 py-2 text-base"
                value={searchValue}
                onChange={e => {
                  setSearchValue(e.target.value);
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.stopPropagation()}
              />
            ) : null}

            {filter()?.map((o, index) =>
              Array.isArray(o) ? (
                <div key={index}>
                  <Separator color="border-gray-800/10" spacingLevel={3} />
                  {o.map((o2, index2) => (
                    <RadixDropdownMenu.Item
                      data-testid={o2.id}
                      id={o2.id}
                      onClick={() => onSelect && onSelect(o2)}
                      key={index2}
                      className={cn(
                        "flex w-full flex-1 cursor-pointer items-center outline-none transition data-highlighted:bg-gray-800/10",
                        { "px-4 py-2": !noItemPadding },
                        itemClassName,
                      )}>
                      <Text className="w-full">{o2.label}</Text>
                    </RadixDropdownMenu.Item>
                  ))}
                </div>
              ) : (
                <RadixDropdownMenu.Item
                  data-testid={o.id}
                  id={o.id}
                  onClick={() => onSelect && onSelect(o)}
                  key={index}
                  className={cn(
                    "flex w-full flex-1 cursor-pointer items-center outline-none transition data-highlighted:bg-gray-800/10",
                    { "px-4 py-2": !noItemPadding },
                    itemClassName,
                  )}>
                  {getOptionTemplate ? getOptionTemplate(o) : <Text className="w-full">{o.label}</Text>}
                </RadixDropdownMenu.Item>
              ),
            )}
          </div>

          {withArrow ? <RadixDropdownMenu.Arrow className="h-2 w-4 fill-white" /> : null}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
};
